import React from 'react'
import {
  Collections as CollectionIcon,
  Description as PostsIcon,
  Email as EmailIcon,
  Facebook as FacebookIcon,
  Image as ImageIcon,
  Language as DeepwebIcon,
  Person as NameIcon,
  Phone as PhoneIcon,
  Search as DefaultIcon,
  Share as SocialIcon,
  VerifiedUser as KycIcon,
} from '@mui/icons-material'
import { api, Response } from '../../function/axios'
import { SearchCategoryCounter, Subscriber } from '../../store/user/reducer'
import s from './UsageCardV2.module.css'

type Period = 'day' | 'week' | 'month' | 'year'

const PERIODS: Array<[Period, string]> = [
  ['day', 'Day'],
  ['week', 'Week'],
  ['month', 'Month'],
  ['year', 'Year'],
]

const WEB = 'var(--primary)'
const API = '#7C5CFF'

// Category labels as the backend sends them in `counters`; anything else gets
// the default icon and color.
const CATEGORY_STYLE: Record<string, { icon: React.ElementType; color: string }> = {
  phones: { icon: PhoneIcon, color: '#2D89F4' },
  emails: { icon: EmailIcon, color: '#8B5CF6' },
  names: { icon: NameIcon, color: '#10B981' },
  posts: { icon: PostsIcon, color: '#F97316' },
  deepweb: { icon: DeepwebIcon, color: '#64748B' },
  'sn id': { icon: SocialIcon, color: '#EC4899' },
  images: { icon: ImageIcon, color: '#F59E0B' },
  kyc: { icon: KycIcon, color: '#0EA5A5' },
  'web collection': { icon: CollectionIcon, color: '#14B8A6' },
  'fb api': { icon: FacebookIcon, color: '#1877F2' },
}

type Props = {
  data: Subscriber
}

export function UsageCardV2({ data }: Props) {
  const [period, setPeriod] = React.useState<Period>('year')
  const [counts, setCounts] = React.useState<{ web: number; api: number } | null>(null)
  const [loading, setLoading] = React.useState(false)

  // Loaded here rather than through the shared common.subscriber state, so
  // switching the period doesn't change the page's KPI cards. Same endpoint
  // and params as getSubscriber in the old page.
  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    api({
      method: 'GET',
      url: '/admin-dashboard/subscriber',
      params: { id: data.id, offset: 0, limit: 100, usage: 'all', time: period },
    })
      .then(({ data: res }: Response) => {
        if (cancelled) return
        setCounts({ web: Number(res?.webUsagesCount) || 0, api: Number(res?.apiUsagesCount) || 0 })
      })
      .catch(() => {
        if (!cancelled) setCounts(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [data.id, period])

  const total = counts ? counts.web + counts.api : null
  const share = (part: number) => (total ? `${Math.round((part / total) * 100)}% of searches` : '—')

  const counters: SearchCategoryCounter[] = Array.isArray(data.counters) ? data.counters : []
  const used = counters.filter((c) => Number(c.counter) > 0).sort((a, b) => Number(b.counter) - Number(a.counter))
  const unused = counters.filter((c) => !(Number(c.counter) > 0))
  const max = used.length ? Number(used[0].counter) : 0
  const categoryTotal = used.reduce((sum, c) => sum + Number(c.counter), 0)

  return (
    <>
      <div className={s.head}>
        <h3>Usage</h3>
        <span className={s.hint}>· {total !== null ? `${total.toLocaleString()} searches` : '…'}</span>
        <span className={s.spacer} />
        <div className={s.seg}>
          {PERIODS.map(([key, label]) => (
            <button key={key} className={period === key ? s.on : ''} onClick={() => setPeriod(key)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={s.body}>
        <div className={`${s.split} ${loading ? s.loading : ''}`}>
          <div className={s.stat}>
            <div className={s.lbl}><i style={{ background: WEB }} />Web</div>
            <b>{counts ? counts.web.toLocaleString() : '—'}</b>
            <span>{counts ? share(counts.web) : ''}</span>
          </div>
          <div className={s.stat}>
            <div className={s.lbl}><i style={{ background: API }} />API</div>
            <b>{counts ? counts.api.toLocaleString() : '—'}</b>
            <span>{counts ? share(counts.api) : ''}</span>
          </div>
          <div className={s.stat}>
            <div className={s.lbl}>Total</div>
            <b>{total !== null ? total.toLocaleString() : '—'}</b>
            <div className={s.stack}>
              {counts && total ? (
                <>
                  <i style={{ width: `${(counts.web / total) * 100}%`, background: WEB }} />
                  <i style={{ width: `${(counts.api / total) * 100}%`, background: API }} />
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div>
          {/* `counters` come with the subscriber record and don't follow the
            * period switch, so the header says so. */}
          <div className={s.secT}>
            <h4>By category</h4>
            <span className={s.hint}>· sorted by use · not affected by the period</span>
          </div>
          {used.length === 0 ? (
            <div className={s.empty}>No searches yet</div>
          ) : (
            <div className={s.rows}>
              {used.map((c) => {
                const style = CATEGORY_STYLE[String(c.label).toLowerCase()] || { icon: DefaultIcon, color: '#64748B' }
                const Icon = style.icon
                const value = Number(c.counter)
                return (
                  <div key={c.label} className={s.row}>
                    <span className={s.ic} style={{ background: `color-mix(in srgb, ${style.color} 14%, transparent)`, color: style.color }}>
                      <Icon sx={{ fontSize: 15 }} />
                    </span>
                    <span className={s.name} title={c.label}>{c.label}</span>
                    <div className={s.bar}><i style={{ width: `${max ? (value / max) * 100 : 0}%`, background: style.color }} /></div>
                    <span className={s.num}>{value.toLocaleString()}</span>
                    <span className={s.pct}>{categoryTotal ? `${Math.round((value / categoryTotal) * 100)}%` : ''}</span>
                  </div>
                )
              })}
            </div>
          )}
          {unused.length > 0 && (
            <div className={s.unused}>
              Not used:
              {unused.map((c) => (
                <span key={c.label} className={s.chip}>{c.label}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
